import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Check, CircleAlert, Copy, KeyRound, Loader2, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { ukDate } from '@/lib/quotes'
import { can } from '@/lib/roles'
import type { StaffUser } from '@/lib/session'
import { reportError } from '@/lib/settings'
import { FormField, ReadOnlyNote, SettingsCard, TextInput } from './form-kit'
import { PageTitle } from './pages'

type Scope = 'read' | 'draft' | 'follow_up' | 'handoff'
interface ApiClient {
  id: string
  name: string
  keyPrefix: string
  scopes: Scope[]
  active: boolean
  lastUsedAt: string | null
  createdAt: string
  version: number
}

const SCOPES: { key: Scope; label: string; description: string }[] = [
  {
    key: 'read',
    label: 'See quotes and activity',
    description: 'Customers, quotes, the price book and what has happened to each quote.',
  },
  {
    key: 'draft',
    label: 'Add customers and draft quotes',
    description: 'Drafts stay as drafts until a person checks, signs and sends them.',
  },
  {
    key: 'follow_up',
    label: 'Send follow-up emails',
    description: 'A polite reminder to clients who have a proposal but have not signed yet.',
  },
  {
    key: 'handoff',
    label: 'Record admin progress',
    description: 'Tick off QuickBooks and SmartTask set-up once a client has signed.',
  },
]

export function IntegrationsPage({ me }: { me: StaffUser }) {
  const admin = can.manageSettings(me)
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['api-clients'],
    queryFn: () => api.get<{ items: ApiClient[] }>('/api-clients'),
    enabled: admin,
  })
  const refresh = () => void qc.invalidateQueries({ queryKey: ['api-clients'] })
  const [name, setName] = useState('Kira')
  const [scopes, setScopes] = useState<Scope[]>(['read', 'draft', 'follow_up', 'handoff'])
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      api.post<{ client: ApiClient; key: string }>('/api-clients', { name: name.trim(), scopes }),
    onSuccess: (r) => {
      setNewKey({ name: r.client.name, key: r.key })
      setCopied(false)
      refresh()
    },
    onError: (e) => reportError(e),
  })
  const update = useMutation({
    mutationFn: (v: { c: ApiClient; active: boolean }) =>
      api.patch<{ client: ApiClient }>(`/api-clients/${v.c.id}`, {
        active: v.active,
        expectedVersion: v.c.version,
      }),
    onSuccess: (r) => {
      refresh()
      toast.success(
        r.client.active
          ? `${r.client.name} can connect again`
          : `${r.client.name}'s key is revoked`,
      )
    },
    onError: (e) => reportError(e, refresh),
  })

  const nameError = name.trim().length < 2 ? 'Enter a name, e.g. Kira.' : null

  return (
    <>
      <PageTitle
        title="Integrations"
        description="Connect Kira, HCD’s assistant, so it can help with quotes and the admin after a client signs."
      />
      {!admin ? (
        <ReadOnlyNote who="an owner or platform admin" />
      ) : (
        <div className="grid gap-5">
          <SettingsCard title="What Kira can do">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <h3 className="mb-2 text-sm font-semibold">Can</h3>
                <ul className="grid gap-1.5 text-sm">
                  {[
                    'Add customers and draft quotes (they stay as drafts)',
                    'See when a quote is sent, opened, accepted and onboarded',
                    'Send follow-up emails to clients who have not signed yet',
                    'Record QuickBooks and SmartTask set-up after a client signs',
                  ].map((t) => (
                    <li key={t} className="flex gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold">Cannot</h3>
                <ul className="grid gap-1.5 text-sm">
                  {[
                    'Sign, send, approve or withdraw a quote',
                    'Change prices in the price book or settings',
                    'See staff accounts, email settings or these keys',
                  ].map((t) => (
                    <li key={t} className="flex gap-2">
                      <X className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </SettingsCard>

          {newKey && (
            <section
              role="alert"
              className="grid gap-3 rounded-xl border border-warning/50 bg-warning/10 p-5"
              data-testid="new-api-key"
            >
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <KeyRound className="size-4" aria-hidden /> Key for {newKey.name}
              </h2>
              <p className="text-sm">
                Copy this key now and put it in Kira’s settings. For security it is{' '}
                <strong>never shown again</strong>. If it is lost, revoke it and make a new one.
              </p>
              <code className="block rounded-lg border bg-card px-3 py-2 text-[13px] break-all">
                {newKey.key}
              </code>
              <div className="flex flex-wrap gap-2">
                <Button
                  className="h-10"
                  onClick={() =>
                    void navigator.clipboard?.writeText(newKey.key).then(() => {
                      setCopied(true)
                      toast.success('Key copied')
                    })
                  }
                >
                  <Copy />
                  {copied ? 'Copied' : 'Copy key'}
                </Button>
                <Button variant="outline" className="h-10" onClick={() => setNewKey(null)}>
                  I have saved it
                </Button>
              </div>
            </section>
          )}

          <SettingsCard
            title="Create a key"
            description="Each assistant or automation gets its own key, so it can be revoked on its own."
          >
            <form
              className="grid gap-5"
              onSubmit={(e) => {
                e.preventDefault()
                if (!nameError && scopes.length) create.mutate()
              }}
            >
              <FormField label="Name" error={nameError}>
                {(p) => (
                  <TextInput
                    {...p}
                    className="sm:max-w-sm"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
              </FormField>
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-sm font-medium">Allowed to</legend>
                {SCOPES.map((s) => (
                  <label
                    key={s.key}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 has-checked:border-primary has-checked:bg-accent/40"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 size-4 accent-(--primary)"
                      checked={scopes.includes(s.key)}
                      onChange={(e) =>
                        setScopes((x) =>
                          e.target.checked ? [...x, s.key] : x.filter((k) => k !== s.key),
                        )
                      }
                    />
                    <span className="grid gap-0.5">
                      <span className="text-sm font-medium">{s.label}</span>
                      <span className="text-[13px] text-muted-foreground">{s.description}</span>
                    </span>
                  </label>
                ))}
                {scopes.length === 0 && (
                  <p className="text-[13px] font-medium text-destructive">Choose at least one.</p>
                )}
              </fieldset>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  className="h-10"
                  disabled={!!nameError || scopes.length === 0 || create.isPending}
                >
                  {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
                  Create key
                </Button>
              </div>
            </form>
          </SettingsCard>

          <SettingsCard title="Keys">
            {q.isError ? (
              <p role="alert" className="flex items-center gap-2 text-sm text-destructive">
                <CircleAlert className="size-4" aria-hidden /> {q.error.message}
              </p>
            ) : !q.data ? (
              <Loader2 className="animate-spin" aria-label="Loading" />
            ) : q.data.items.length === 0 ? (
              <p className="text-[15px] text-muted-foreground">No keys yet.</p>
            ) : (
              <ul className="grid gap-3" aria-label="API keys">
                {q.data.items.map((c) => (
                  <li
                    key={c.id}
                    className="grid gap-3 rounded-lg border p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                  >
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 font-medium">
                        <Bot className="size-4 text-primary" aria-hidden />
                        {c.name}
                        {c.active ? (
                          <Badge className="border-success/30 bg-success/10 text-[#0f5c3b] dark:text-success">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Revoked</Badge>
                        )}
                      </p>
                      <p className="mt-1 text-[13px] break-all text-muted-foreground">
                        <code>{c.keyPrefix}…</code> · created {ukDate(c.createdAt)} ·{' '}
                        {c.lastUsedAt ? `last used ${ukDate(c.lastUsedAt, true)}` : 'not used yet'}
                      </p>
                      <p className="mt-1 text-[13px] text-muted-foreground">
                        {SCOPES.filter((s) => c.scopes.includes(s.key))
                          .map((s) => s.label)
                          .join(' · ')}
                      </p>
                    </div>
                    <Button
                      variant={c.active ? 'outline' : 'secondary'}
                      className="h-10 justify-self-start"
                      disabled={update.isPending}
                      onClick={() => {
                        if (
                          c.active &&
                          !window.confirm(`Revoke ${c.name}'s key? It stops working straight away.`)
                        )
                          return
                        update.mutate({ c, active: !c.active })
                      }}
                    >
                      {c.active ? 'Revoke' : 'Turn back on'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </SettingsCard>
        </div>
      )}
    </>
  )
}
